import {requestAccountExit} from "../hooks/useUnsavedChanges";
import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canOpenRoute, ROLE_LABELS } from "../lib/permissions";
import { api, apiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import PendingOperations from './PendingOperations';
import ThemeToggle from './ThemeToggle';
import SiteSwitcher from './SiteSwitcher';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Settings, LogOut, Bell, Menu, ChevronRight, User as UserIcon, Search, Activity } from "lucide-react";
import { MENU } from "../config/navigation";
import CommandPalette, { useCommandPaletteShortcut } from "./CommandPalette";



function NavItems({ user, onNavigate, prefix = "nav-" }) {
  return (
    <nav className="flex flex-col gap-1 px-3 py-2">
      {MENU.filter((m) => canOpenRoute(user, m.to)).map((m) => (
        <NavLink
          key={m.to}
          to={m.to}
          end={m.to === "/"}
          onClick={onNavigate}
          data-testid={`${prefix}${m.label.toLowerCase().replace(/\s|\//g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <m.icon className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden="true" />
          <span className="truncate">{m.to === "/utenti" ? "Utenti e permessi" : m.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState("");
  const [alertsRetry, setAlertsRetry] = useState(0);
  const alertCount = alerts.length;
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteShortcut(setPaletteOpen);

  useEffect(() => {
    let active = true;
    setAlertsLoading(true); setAlertsError("");
    api.get("/notifications").then((r) => { if (active) setAlerts(r.data); })
      .catch((e) => { if (active) { setAlerts([]); setAlertsError(apiError(e)); } })
      .finally(() => { if (active) setAlertsLoading(false); });
    return () => { active = false; };
  }, [location.pathname, alertsOpen, alertsRetry]);

  // On phones the whole page scrolls, so a new section starts from the top.
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const current = MENU.find((m) => m.to === location.pathname);
  const crumb = current ? current.label : location.pathname.startsWith("/veicoli/") ? "Dettaglio veicolo" : "";

  return (
    <div className="flex min-h-dvh bg-background lg:h-screen lg:overflow-hidden">
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <Dialog open={alertsOpen} onOpenChange={setAlertsOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader><DialogTitle>Notifiche</DialogTitle><DialogDescription>Scadenze, manutenzioni e avvisi della flotta.</DialogDescription></DialogHeader>
          {alertsLoading ? <p role="status">Caricamento notifiche…</p> : alertsError ? (
            <div role="alert"><p>{alertsError}</p><Button className="mt-3" onClick={() => setAlertsRetry((n) => n + 1)}>Riprova</Button></div>
          ) : alerts.length === 0 ? <p>Nessuna notifica.</p> : (
            <ul className="space-y-3">{alerts.map((alert) => <li key={alert.id + alert.type} className="rounded-lg border border-border p-3"><p className="text-sm font-medium">{alert.title}</p><p className="text-sm text-muted-foreground">{alert.message}</p></li>)}</ul>
          )}
        </DialogContent>
      </Dialog>
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-16 items-center border-b border-border px-5">
          <Link to="/"><Logo /></Link>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavItems user={user} />
        </div>
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="flex w-72 flex-col gap-0 border-border bg-card p-0">
          <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
          <SheetDescription className="sr-only">Scegli la sezione del gestionale da aprire.</SheetDescription>
          <div className="flex h-16 shrink-0 items-center border-b border-border px-5">
            <Logo />
          </div>
          {/* The logo stays put; the list scrolls when it is taller than the phone screen. */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]" data-testid="mobile-nav-scroll">
            <NavItems user={user} onNavigate={() => setMobileOpen(false)} prefix="mnav-" />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col lg:overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 py-2 lg:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Apri menu di navigazione" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
            {user.tenant_id && <p className="truncate text-sm font-semibold" title={user.tenant_name || user.tenant_id} data-testid="current-company"><span className="sr-only">Azienda: </span>{user.tenant_name || user.tenant_id}</p>}
            <div className="flex min-w-0 items-center gap-1.5 text-sm">
              <Link to="/" className="text-muted-foreground hover:text-foreground">AutoTu</Link>
              {crumb && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              {crumb && <span className="truncate font-medium text-foreground">{crumb}</span>}
            </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <SiteSwitcher />
            <Button variant="outline" className="hidden h-9 gap-2 px-3 text-muted-foreground md:inline-flex" onClick={() => setPaletteOpen(true)} data-testid="command-palette-btn">
              <Search aria-hidden="true" />Cerca…<kbd className="ml-2 rounded border border-border px-1.5 text-[11px] font-medium">{typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "") ? "⌘K" : "Ctrl K"}</kbd>
            </Button>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="Cerca nell’app" onClick={() => setPaletteOpen(true)}><Search className="h-5 w-5" /></Button>
            <ThemeToggle />
            <Button variant="ghost" size="icon" className="relative" aria-label="Apri notifiche" onClick={() => setAlertsOpen(true)} data-testid="header-alerts-btn">
              <Bell className="h-5 w-5" />
              {alertCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                  {alertCount > 99 ? "99+" : alertCount}
                </span>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" aria-label={`Menu account: ${user.name}`} data-testid="user-menu-btn">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <UserIcon className="h-4 w-4" />
                  </div>
                  <div className="hidden text-left leading-tight sm:block">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/attivita")} data-testid="menu-activity">
                  <Activity className="mr-2 h-4 w-4" /> Attività
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/impostazioni")} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" /> Impostazioni
                </DropdownMenuItem>
                <DropdownMenuItem onClick={()=>requestAccountExit(logout)} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:overflow-y-auto lg:p-6"><PendingOperations/>{children}</main>
      </div>
    </div>
  );
}
